import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/layout/SectionHeader';
import { LoadingState } from '@/components/states/LoadingState';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { formatDate, formatNumber } from '@/lib/utils';
import { useMonitoringOverview } from './hooks/useMonitoringOverview';
import PipelineStatusCard from './components/PipelineStatusCard';
import MetricsChart from './components/MetricsChart';
import LogViewer from './components/LogViewer';
import AlertsList from './components/AlertsList';
import { MonitoringOverview } from './types/monitoring';
import { Activity, RefreshCw } from 'lucide-react';

const emptyOverview: MonitoringOverview = {
  generatedAt: new Date().toISOString(),
  metrics: {
    totalPipelines: 0,
    runningPipelines: 0,
    successRate: 0,
    avgDurationSec: 0,
    recordsProcessed: 0,
  },
  series: [],
  pipelines: [],
  alerts: [],
  logs: [],
};

export default function MonitoringPage() {
  const { data, isLoading, isError, refetch } = useMonitoringOverview();
  const overview = data ?? emptyOverview;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Monitoring Grid"
        subtitle="Observe service health, throughput, and alert conditions across all active workloads."
        metric={isError ? 'Unavailable' : 'Live'}
        actions={(
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>Updated {formatDate(overview.generatedAt)}</span>
        {isLoading && <Badge variant="secondary">Refreshing…</Badge>}
        {isError && <Badge variant="destructive">Unavailable</Badge>}
      </div>

      {isError && !data ? (
        <ErrorState
          description="Monitoring metrics cannot be loaded right now. Verify API and try refresh."
          action={(
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          )}
        />
      ) : null}

      <div data-testid="monitoring-health-grid" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>Total Pipelines</CardTitle>
            <CardDescription>Active workflows</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{overview.metrics.totalPipelines}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Running Now</CardTitle>
            <CardDescription>In-flight executions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{overview.metrics.runningPipelines}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Success Rate</CardTitle>
            <CardDescription>Last hour</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{overview.metrics.successRate}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Records Processed</CardTitle>
            <CardDescription>Latest interval</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {formatNumber(overview.metrics.recordsProcessed)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Processing Trends</CardTitle>
          <CardDescription>Records processed vs success rate</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState
              title="Refreshing metrics"
              description="Collecting the latest throughput and reliability trend data."
            />
          ) : overview.series.length === 0 ? (
            <EmptyState
              title="No trend metrics yet"
              description="Processing trends will appear after pipelines start producing runtime data."
              icon={<Activity className="h-6 w-6 text-muted-foreground" />}
            />
          ) : (
            <MetricsChart series={overview.series} />
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Pipeline Status</h2>
          {overview.pipelines.length === 0 ? (
            <EmptyState
              title="No pipelines to display"
              description="Pipeline health cards will appear once monitoring receives active pipeline records."
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {overview.pipelines.map((pipeline) => (
                <PipelineStatusCard key={pipeline.id} pipeline={pipeline} />
              ))}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Alerts</h2>
          <AlertsList alerts={overview.alerts} />
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold">Recent Logs</h2>
        {overview.logs.length === 0 ? (
          <EmptyState
            title="No logs collected"
            description="Log stream is empty for the current monitoring window."
          />
        ) : (
          <LogViewer logs={overview.logs} />
        )}
      </div>
    </div>
  );
}
